import { useId, useRef, useState } from 'react';
import { formatNumber, monthFullLabel, monthLabel } from '@/lib/dashboardFormat';

export interface TrendPoint {
  /** YYYY-MM. */
  month: string;
  value: number;
  /** Прирост за месяц — показывается в подсказке точки. */
  delta?: number;
}

const VIEW_W = 600;
const VIEW_H = 180;
const PAD_TOP = 16;
const PAD_BOTTOM = 26;

/**
 * Линия с заливкой — накопительный размер портфеля по месяцам.
 *
 * Инлайновый SVG в единицах viewBox с preserveAspectRatio="none" по ширине:
 * график тянется по карточке, а толщина линии и подписи заданы в пикселях
 * через vector-effect и обычный текст, поэтому не растягиваются.
 *
 * Значение показывается при наведении на любую точку графика, а не только на
 * саму точку: ближайший месяц вычисляется по горизонтали от курсора — попадать
 * мышью в кружок диаметром несколько пикселей неудобно.
 *
 * Своя реализация вместо библиотеки графиков: нужен ровно один тип графика,
 * тянуть ради него зависимость в бандл незачем.
 */
export function TrendChart({ points, ariaLabel }: { points: TrendPoint[]; ariaLabel: string }) {
  const gradientId = useId();
  const plotRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  if (points.length === 0) {
    return <p className="chart__empty">Пока нет данных за этот период.</p>;
  }

  const values = points.map((p) => p.value);
  const max = Math.max(...values);
  const min = Math.min(...values);
  // Плоская линия (все значения равны) не должна прилипать к нижнему краю.
  const top = max === min ? max + 1 : max;
  const bottom = max === min ? Math.max(0, min - 1) : min;

  const stepX = points.length > 1 ? VIEW_W / (points.length - 1) : 0;
  const plotH = VIEW_H - PAD_TOP - PAD_BOTTOM;
  const baseline = PAD_TOP + plotH;

  const coords = points.map((point, i) => ({
    ...point,
    x: points.length > 1 ? i * stepX : VIEW_W / 2,
    y: PAD_TOP + plotH - ((point.value - bottom) / (top - bottom)) * plotH,
  }));

  const line = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x},${c.y}`).join(' ');
  // Заливка — та же линия, замкнутая по нижней границе области графика.
  const first = coords[0];
  const last = coords[coords.length - 1];
  const area = `${line} L${last.x},${baseline} L${first.x},${baseline} Z`;

  /** Ближайший по горизонтали месяц — курсору не нужно попадать в саму точку. */
  const trackCursor = (clientX: number) => {
    const rect = plotRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;

    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    setActiveIndex(Math.round(ratio * (points.length - 1)));
  };

  const active = activeIndex !== null ? coords[activeIndex] : null;
  const activeRatio = points.length > 1 && activeIndex !== null ? activeIndex / (points.length - 1) : 0.5;

  return (
    <div className="chart">
      <div
        className="chart__plot"
        ref={plotRef}
        onMouseMove={(e) => trackCursor(e.clientX)}
        onMouseLeave={() => setActiveIndex(null)}
      >
        <svg
          className="chart__svg"
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={ariaLabel}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" className="chart__fill-from" />
              <stop offset="100%" className="chart__fill-to" />
            </linearGradient>
          </defs>

          {/* Три опорных линии сетки: без них глазу не за что зацепиться. */}
          {[0, 0.5, 1].map((ratio) => (
            <line
              key={ratio}
              className="chart__grid"
              x1={0}
              x2={VIEW_W}
              y1={PAD_TOP + plotH * ratio}
              y2={PAD_TOP + plotH * ratio}
              vectorEffect="non-scaling-stroke"
            />
          ))}

          <path d={area} fill={`url(#${gradientId})`} />
          <path className="chart__line" d={line} vectorEffect="non-scaling-stroke" />

          {active && (
            <line
              className="chart__cursor"
              x1={active.x}
              x2={active.x}
              y1={PAD_TOP}
              y2={baseline}
              vectorEffect="non-scaling-stroke"
            />
          )}

          {coords.map((c, i) => (
            <circle
              key={c.month}
              className={`chart__dot${i === activeIndex ? ' chart__dot--active' : ''}`}
              cx={c.x}
              cy={c.y}
              r={i === activeIndex ? 5 : 3.5}
            />
          ))}
        </svg>

        {active && (
          <div
            className="chart__tooltip"
            style={{ left: `${activeRatio * 100}%` }}
            role="status"
            aria-live="polite"
          >
            <span className="chart__tooltip-month">{monthFullLabel(active.month)}</span>
            <span className="chart__tooltip-value">{formatNumber(active.value)} клиентов</span>
            {active.delta !== undefined && (
              <span className="chart__tooltip-delta">
                {active.delta > 0 ? `+${active.delta} за месяц` : 'без изменений'}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="chart__axis">
        {points.map((p, i) => (
          <span key={p.month} className={i === activeIndex ? 'chart__axis-label--active' : undefined}>
            {monthLabel(p.month)}
          </span>
        ))}
      </div>
    </div>
  );
}
